import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Row, Col, Typography, Space, Spin, message,
  Tag, Progress, Modal, Form, Input, Select, Slider, List, Empty,
} from 'antd';
import {
  SafetyCertificateOutlined, AimOutlined, BulbOutlined, RocketOutlined,
  SoundOutlined, DollarOutlined, CarOutlined, TeamOutlined,
  HeartOutlined, UserOutlined, BankOutlined, DatabaseOutlined,
  EditOutlined, CheckCircleOutlined, ClockCircleOutlined,
  PlayCircleOutlined, MinusCircleOutlined, PlusOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { businessModuleAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const MODULE_ICONS = {
  governance_legal: <SafetyCertificateOutlined />,
  strategy: <AimOutlined />,
  product_readiness: <BulbOutlined />,
  product_launch: <RocketOutlined />,
  demand_gen: <SoundOutlined />,
  sales_close: <DollarOutlined />,
  delivery_onsite: <CarOutlined />,
  delivery_ongoing: <TeamOutlined />,
  customer_relationship: <HeartOutlined />,
  people_culture: <UserOutlined />,
  finance: <BankOutlined />,
  data_systems: <DatabaseOutlined />,
};

const STATUS_CONFIG = {
  not_started: { label: '未開始', color: '#d9d9d9', icon: <MinusCircleOutlined /> },
  planning: { label: '規劃中', color: '#faad14', icon: <ClockCircleOutlined /> },
  in_progress: { label: '進行中', color: '#1890ff', icon: <PlayCircleOutlined /> },
  completed: { label: '已完成', color: '#52c41a', icon: <CheckCircleOutlined /> },
};

const LAYER_STYLES = {
  governance: {
    bg: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
    border: '#d3adf7',
    accent: '#722ed1',
    title: '治理與方向層',
    subtitle: '頂層設計 / Top View',
  },
  business_process: {
    bg: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
    border: '#91d5ff',
    accent: '#1890ff',
    title: '現金流閉環',
    subtitle: '業務核心運營循環 / Business Process',
  },
  resources: {
    bg: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
    border: '#b7eb8f',
    accent: '#52c41a',
    title: '成長與資源中心',
    subtitle: 'Resources Center',
  },
};

export default function BusinessModuleMapPage() {
  const [modules, setModules] = useState([]);
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchMap = useCallback(async () => {
    setLoading(true);
    try {
      const res = await businessModuleAPI.getMap();
      setModules(res.data.modules || []);
      setLayers(res.data.layers || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMap(); }, [fetchMap]);

  const openEdit = (mod) => {
    setEditModal(mod);
    form.setFieldsValue({
      status: mod.status || 'not_started',
      progress: mod.progress || 0,
      notes: mod.notes || '',
    });
  };

  const handleSave = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      const values = form.getFieldsValue();
      await businessModuleAPI.updateModule(editModal.key, values);
      message.success('已更新');
      setEditModal(null);
      fetchMap();
    } catch (e) {
      message.error('更新失敗：' + (e.message || ''));
    }
    setSaving(false);
  };

  const getModulesByLayer = (layerKey) => modules.filter(m => m.layer === layerKey);

  const overallProgress = modules.length > 0
    ? Math.round(modules.reduce((sum, m) => sum + (m.progress || 0), 0) / modules.length)
    : 0;

  const statusCounts = modules.reduce((acc, m) => {
    acc[m.status] = (acc[m.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  const renderModuleCard = (mod) => {
    const sc = STATUS_CONFIG[mod.status] || STATUS_CONFIG.not_started;
    const icon = MODULE_ICONS[mod.key];

    return (
      <Col key={mod.key} xs={24} sm={12} md={8} lg={6}>
        <Card
          hoverable
          size="small"
          onClick={() => openEdit(mod)}
          style={{
            borderLeft: `4px solid ${sc.color}`,
            height: '100%',
          }}
          styles={{ body: { padding: '12px 16px' } }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{
              fontSize: 24, color: LAYER_STYLES[mod.layer]?.accent || '#999',
              flexShrink: 0, marginTop: 2,
            }}>
              {icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{mod.name}</div>
              <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>{mod.name_en}</div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8, lineHeight: 1.4 }}>
                {mod.description}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tag color={sc.color} icon={sc.icon} style={{ margin: 0 }}>{sc.label}</Tag>
                {mod.progress > 0 && (
                  <Progress
                    percent={mod.progress}
                    size="small"
                    style={{ flex: 1, marginBottom: 0 }}
                    strokeColor={sc.color}
                  />
                )}
              </div>
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  const renderLayer = (layerKey) => {
    const style = LAYER_STYLES[layerKey];
    const mods = getModulesByLayer(layerKey);
    if (mods.length === 0) return null;

    return (
      <Card
        key={layerKey}
        style={{
          marginBottom: 24,
          background: style.bg,
          borderColor: style.border,
        }}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 18, color: style.accent }}>{style.title}</Text>
          <Text type="secondary" style={{ marginLeft: 12, fontSize: 13 }}>{style.subtitle}</Text>
        </div>
        <Row gutter={[12, 12]}>
          {mods.map(renderModuleCard)}
        </Row>
      </Card>
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>12 模組全局地圖</Title>
        <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 16 }}>
          產品 → 訂單 → 交付 → 客戶 → 再回到產品。模組的目的：縮短這個循環，先幫客戶賺到第一筆錢。
        </Paragraph>

        <Row gutter={16}>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Progress type="circle" percent={overallProgress} size={60} />
              <div style={{ marginTop: 8, fontWeight: 600 }}>整體進度</div>
            </Card>
          </Col>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <Col xs={6} sm={4} md={3} key={key}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: cfg.color }}>{statusCounts[key] || 0}</div>
                <div style={{ fontSize: 12 }}>{cfg.label}</div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Business cycle flow indicator */}
      <div style={{
        background: '#fff',
        borderRadius: 8,
        padding: '12px 20px',
        marginBottom: 24,
        textAlign: 'center',
        border: '1px solid #f0f0f0',
      }}>
        <Space size={4} wrap style={{ fontSize: 15, fontWeight: 600 }}>
          <Tag color="blue">產品</Tag> → <Tag color="cyan">訂單</Tag> → <Tag color="green">交付</Tag> → <Tag color="orange">客戶</Tag> → <Tag color="blue">再回到產品</Tag>
        </Space>
      </div>

      {renderLayer('governance')}
      {renderLayer('business_process')}
      {renderLayer('resources')}

      {/* Edit Modal */}
      <Modal
        title={editModal ? `${editModal.name} (${editModal.name_en})` : ''}
        open={!!editModal}
        onCancel={() => setEditModal(null)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="儲存"
        width={600}
      >
        {editModal && (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              {editModal.description}
            </Paragraph>
            <Form form={form} layout="vertical">
              <Form.Item name="status" label="狀態">
                <Select
                  options={Object.entries(STATUS_CONFIG).map(([v, c]) => ({
                    value: v, label: c.label,
                  }))}
                />
              </Form.Item>
              <Form.Item name="progress" label="完成進度 (%)">
                <Slider min={0} max={100} step={5}
                  marks={{ 0: '0%', 25: '25%', 50: '50%', 75: '75%', 100: '100%' }} />
              </Form.Item>
              <Form.Item name="notes" label="備註">
                <TextArea rows={4} placeholder="紀錄當前進度、待辦事項、關鍵決策..." />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
}
